import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Privacy Policy | ALIGN&bull;</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: "#F7F5F0" }} data-testid="privacy-policy-page">
        {/* Header */}
        <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="text-[#5C635A] hover:text-[#4A6741] transition-colors" data-testid="privacy-back-button">
                <ArrowLeft size={18} />
              </button>
              <button onClick={() => navigate("/")} className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight text-[#1A1C19] hover:text-[#4A6741] transition-colors">
                ALIGN<span className="text-[#4A6741]">&bull;</span>
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">
              Legal
            </p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-4 leading-[1.1]">
              Privacy Policy
            </h1>
            <p className="text-sm font-light text-[#5C635A] mb-12">
              Effective Date: 13 May 2026
            </p>

            <div className="prose-align space-y-10">
              {/* Intro */}
              <p className="text-base font-light text-[#5C635A] leading-relaxed">
                ALIGN&bull; (&ldquo;ALIGN&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) is committed to protecting your privacy and handling your personal information with care, transparency, and respect.
              </p>
              <p className="text-base font-light text-[#5C635A] leading-relaxed">
                This Privacy Policy outlines how we collect, use, store, and protect your information when you visit{" "}
                <a href="https://www.alignpraxis.com.au" className="text-[#4A6741] underline decoration-[#4A6741]/30 hover:decoration-[#4A6741] transition-colors">www.alignpraxis.com.au</a>,
                join our waitlist, download resources, contact us, or engage with our services.
              </p>

              {/* 1 */}
              <Section number="1" title="Who We Are">
                <p>ALIGN&bull; is a psychology and wellbeing practice currently being established in Perth, Western Australia.</p>
                <div className="mt-4 space-y-1">
                  <p><a href="mailto:hello@alignpraxis.com.au" className="text-[#4A6741] underline decoration-[#4A6741]/30 hover:decoration-[#4A6741] transition-colors">hello@alignpraxis.com.au</a></p>
                  <p><a href="https://www.alignpraxis.com.au" className="text-[#4A6741] underline decoration-[#4A6741]/30 hover:decoration-[#4A6741] transition-colors">www.alignpraxis.com.au</a></p>
                </div>
              </Section>

              {/* 2 */}
              <Section number="2" title="Information We Collect">
                <p>We may collect personal information that you voluntarily provide, including:</p>
                <ul>
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Information submitted through forms or waitlists</li>
                  <li>Information you provide when contacting us</li>
                  <li>Website usage information through cookies and analytics tools</li>
                </ul>
                <p className="mt-4">If clinical services commence in the future, additional health and sensitive information may be collected in accordance with applicable Australian privacy and healthcare legislation.</p>
              </Section>

              {/* 3 */}
              <Section number="3" title="How We Use Your Information">
                <p>We may use your information to:</p>
                <ul>
                  <li>Respond to enquiries</li>
                  <li>Provide updates about ALIGN&bull;</li>
                  <li>Manage our waitlist</li>
                  <li>Send resources, newsletters, or relevant information</li>
                  <li>Improve website experience and functionality</li>
                  <li>Understand audience needs and engagement</li>
                  <li>Prepare for future service delivery</li>
                </ul>
                <p className="mt-4">We do not sell or rent your personal information to third parties.</p>
              </Section>

              {/* 4 */}
              <Section number="4" title="Email Communications">
                <p>If you subscribe to our waitlist or resources, you may receive occasional emails related to:</p>
                <ul>
                  <li>Practice updates</li>
                  <li>Educational resources</li>
                  <li>Mental health and wellbeing content</li>
                  <li>Launch announcements</li>
                  <li>Relevant ALIGN&bull; offerings</li>
                </ul>
                <p className="mt-4">You may unsubscribe at any time using the unsubscribe link included in emails or by contacting us directly.</p>
              </Section>

              {/* 5 */}
              <Section number="5" title="Cookies & Website Analytics">
                <p>Our website may use cookies and analytics tools (such as Google Analytics or Meta Pixel) to help us understand how visitors use the site and improve user experience.</p>
                <p className="mt-4">This may include information such as:</p>
                <ul>
                  <li>Browser type</li>
                  <li>Device information</li>
                  <li>Pages visited</li>
                  <li>Time spent on the site</li>
                  <li>Referral sources</li>
                </ul>
                <p className="mt-4">You can manage or disable cookies through your browser settings.</p>
              </Section>

              {/* 6 */}
              <Section number="6" title="Data Security">
                <p>We take reasonable steps to protect your personal information from misuse, loss, unauthorised access, disclosure, or alteration.</p>
                <p className="mt-4">While we strive to use commercially acceptable means to protect your information, no online transmission or storage system can be guaranteed as completely secure.</p>
              </Section>

              {/* 7 */}
              <Section number="7" title="Third-Party Services">
                <p>Our website may contain links to external websites or use third-party platforms for:</p>
                <ul>
                  <li>Email marketing</li>
                  <li>Website hosting</li>
                  <li>Booking systems</li>
                  <li>Analytics</li>
                  <li>Payment processing</li>
                </ul>
                <p className="mt-4">These services may have their own privacy policies and practices.</p>
              </Section>

              {/* 8 */}
              <Section number="8" title="Access & Correction">
                <p>You may request access to personal information we hold about you and request corrections if needed.</p>
                <p className="mt-4">
                  To make a request, contact:{" "}
                  <a href="mailto:hello@alignpraxis.com.au" className="text-[#4A6741] underline decoration-[#4A6741]/30 hover:decoration-[#4A6741] transition-colors">hello@alignpraxis.com.au</a>
                </p>
              </Section>

              {/* 9 */}
              <Section number="9" title="Future Clinical Services">
                <p>As ALIGN&bull; develops into an operational psychology practice, additional privacy obligations under:</p>
                <ul>
                  <li>the Privacy Act 1988 (Cth)</li>
                  <li>Australian Privacy Principles (APPs)</li>
                  <li>relevant healthcare record legislation</li>
                </ul>
                <p className="mt-4">may apply.</p>
                <p className="mt-4">Any future clinical services will operate in accordance with professional, ethical, and legal confidentiality requirements.</p>
              </Section>

              {/* 10 */}
              <Section number="10" title="Changes to This Policy">
                <p>We may update this Privacy Policy from time to time to reflect operational, legal, or service changes.</p>
                <p className="mt-4">Updated versions will be published on this website with the revised effective date.</p>
              </Section>

              {/* 11 */}
              <Section number="11" title="Contact">
                <p>If you have questions about this Privacy Policy or how your information is handled, please contact:</p>
                <div className="mt-4 space-y-1">
                  <p className="font-medium text-[#1A1C19]">ALIGN&bull;</p>
                  <p><a href="mailto:hello@alignpraxis.com.au" className="text-[#4A6741] underline decoration-[#4A6741]/30 hover:decoration-[#4A6741] transition-colors">hello@alignpraxis.com.au</a></p>
                  <p><a href="https://www.alignpraxis.com.au" className="text-[#4A6741] underline decoration-[#4A6741]/30 hover:decoration-[#4A6741] transition-colors">www.alignpraxis.com.au</a></p>
                </div>
                <p className="mt-6 font-['Cormorant_Garamond'] text-xl italic text-[#4A6741]">
                  Psychology, in context.
                </p>
              </Section>
            </div>

            {/* Back to site */}
            <div className="mt-16 pt-8 border-t border-[#EAE6DF]">
              <button
                onClick={() => navigate("/")}
                className="text-xs text-[#5C635A] hover:text-[#4A6741] transition-colors inline-flex items-center gap-2"
              >
                <ArrowLeft size={12} />
                Back to ALIGN&bull;
              </button>
            </div>
          </motion.div>
        </main>
      </div>
    </>
  );
}

function Section({ number, title, children }) {
  return (
    <div>
      <h2 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-normal text-[#1A1C19] mb-4">
        <span className="text-[#4A6741] font-['Manrope'] text-sm font-medium mr-3">{number}.</span>
        {title}
      </h2>
      <div className="text-base font-light text-[#5C635A] leading-relaxed space-y-0 [&_ul]:mt-3 [&_ul]:space-y-2 [&_ul]:list-none [&_li]:relative [&_li]:pl-5 [&_li]:before:content-[''] [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-[10px] [&_li]:before:w-1 [&_li]:before:h-1 [&_li]:before:rounded-full [&_li]:before:bg-[#4A6741]">
        {children}
      </div>
    </div>
  );
}
